import { useEffect, useRef, useState } from "react"
import { Camera, ImagePlus } from "lucide-react"
import { PhotoCropEditor } from "~/components/PhotoCropEditor"

const MAX_MB = 10
const ACCEPT = "image/jpeg,image/png,image/heic,image/heif,.heic,.heif"

interface PhotoUploadPanelProps {
  dogName: string
  photo: File | null
  previewUrl: string | null
  error: string | null
  onPhotoSelected: (file: File, previewUrl: string) => void
  onClear: () => void
}

export function PhotoUploadPanel({
  dogName,
  photo,
  previewUrl,
  error,
  onPhotoSelected,
  onClear,
}: PhotoUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [processing, setProcessing] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
    }
  }, [pendingPreviewUrl])

  function clearPending() {
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
    setPendingFile(null)
    setPendingPreviewUrl(null)
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setProcessing(true)
    try {
      const normalized = await normalizePhotoFile(file)
      if (normalized.size > MAX_MB * 1024 * 1024) {
        throw new Error(`Photo is too large. Maximum size is ${MAX_MB}MB.`)
      }
      clearPending()
      setPendingFile(normalized)
      setPendingPreviewUrl(URL.createObjectURL(normalized))
    } catch {
      onClear()
      if (inputRef.current) inputRef.current.value = ""
    } finally {
      setProcessing(false)
    }
  }

  function handleCropConfirm(file: File, croppedPreviewUrl: string) {
    clearPending()
    onPhotoSelected(file, croppedPreviewUrl)
    if (inputRef.current) inputRef.current.value = ""
  }

  function handleCropCancel() {
    clearPending()
    if (inputRef.current) inputRef.current.value = ""
  }

  function handleChooseDifferent() {
    onClear()
    clearPending()
    if (inputRef.current) inputRef.current.value = ""
  }

  return (
    <section className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 shadow-sm">
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
        Add a photo of {dogName}
      </h2>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        We&apos;ll use this to create a personalised portrait and tailor your Doggy Dynamo experience.
      </p>

      {pendingFile && pendingPreviewUrl ? (
        <PhotoCropEditor
          imageSrc={pendingPreviewUrl}
          fileName={pendingFile.name}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      ) : previewUrl ? (
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="aspect-[16/10] w-full overflow-hidden rounded-lg">
            <img
              src={previewUrl}
              alt={`Preview of ${dogName}`}
              className="h-full w-full object-cover object-center"
            />
          </div>
          <button
            type="button"
            onClick={handleChooseDifferent}
            className="text-sm font-medium text-[var(--color-text-primary)] underline"
          >
            Choose a different photo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={processing}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-text-primary)] px-4 py-3 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:opacity-60"
        >
          <ImagePlus size={18} aria-hidden />
          {processing ? "Processing…" : "Upload a Photo"}
        </button>
      )}

      <p className="mt-3 flex items-center justify-center gap-1 text-xs text-[var(--color-text-secondary)]">
        <Camera size={14} aria-hidden />
        JPG, PNG, or HEIC · Max {MAX_MB}MB
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          handleFileChange(e).catch((err) => {
            console.error(err)
          })
        }}
      />

      {!photo && !pendingFile && (
        <p className="sr-only" aria-live="polite">
          Photo required before creating account
        </p>
      )}

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}

async function normalizePhotoFile(file: File): Promise<File> {
  return file
}
